import React, { useEffect, useRef, useState } from 'react';
import { Search, X, Building2, Briefcase, Home, User as UserIcon } from 'lucide-react';
import { supabase } from '../../supabaseClient';

const ENTITY_META = {
  empresa: { icon: Building2, label: 'Empresa', placeholder: 'Buscar empresa (mín. 2 letras)...' },
  oferta:  { icon: Briefcase, label: 'Oferta',  placeholder: 'Buscar oferta (mín. 2 letras)...' },
  imovel:  { icon: Home,      label: 'Imóvel',  placeholder: 'Buscar imóvel (mín. 2 letras)...' },
  contato: { icon: UserIcon,  label: 'Contato', placeholder: 'Buscar contato (nome/email)...' },
};

async function searchEntities(entityType, term) {
  const padrao = `%${term}%`;
  if (entityType === 'empresa') {
    const { data, error } = await supabase
      .from('empresas')
      .select('id, empresa, company_name')
      .or(`empresa.ilike.${padrao},company_name.ilike.${padrao}`)
      .limit(8);
    if (error) throw error;
    return (data || []).map((r) => ({
      type: 'empresa',
      id: r.id,
      label: r.company_name || r.empresa || r.id,
      sub: null,
      raw: r,
    }));
  }
  if (entityType === 'oferta') {
    const { data, error } = await supabase
      .from('ofertas')
      .select('id, nome, empresa_id, empresas:empresa_id (id, empresa, company_name)')
      .ilike('nome', padrao)
      .limit(8);
    if (error) throw error;
    return (data || []).map((r) => ({
      type: 'oferta',
      id: r.id,
      label: r.nome || r.id,
      sub: r.empresas?.company_name || r.empresas?.empresa || null,
      raw: r,
    }));
  }
  if (entityType === 'imovel') {
    const { data, error } = await supabase
      .from('imoveis')
      .select('id, nome')
      .ilike('nome', padrao)
      .limit(8);
    if (error) throw error;
    return (data || []).map((r) => ({
      type: 'imovel',
      id: r.id,
      label: r.nome || r.id,
      sub: null,
      raw: r,
    }));
  }
  if (entityType === 'contato') {
    const { data, error } = await supabase
      .from('contatos')
      .select('id, first_name, last_name, email, empresa_id')
      .or(`first_name.ilike.${padrao},last_name.ilike.${padrao},email.ilike.${padrao}`)
      .limit(8);
    if (error) throw error;
    return (data || []).map((r) => {
      const name = `${r.first_name || ''} ${r.last_name || ''}`.trim();
      return {
        type: 'contato',
        id: r.id,
        label: name || r.email || r.id,
        sub: name ? r.email : null,
        raw: r,
      };
    });
  }
  return [];
}

export default function EntitySearchField({ entityType, value, onChange, autoFocus = false, className = '' }) {
  const meta = ENTITY_META[entityType] || ENTITY_META.empresa;
  const Icon = meta.icon;

  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setError(null);
    const q = term.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const hits = await searchEntities(entityType, q);
        setResults(hits);
      } catch (err) {
        setError(err.message || 'Erro na busca');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [term, entityType]);

  useEffect(() => {
    function handleOutside(e) {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const handlePick = (item) => {
    onChange?.(item);
    setTerm('');
    setOpen(false);
  };

  const handleClear = () => {
    onChange?.(null);
    setTerm('');
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <label className="block text-xs font-medium text-slate-700 mb-1 flex items-center gap-1">
        <Icon size={12} className="text-slate-500" /> {meta.label}
      </label>

      {value ? (
        <div className="flex items-center justify-between gap-2 px-3 py-2 border border-emerald-300 bg-emerald-50 rounded-lg text-sm">
          <div className="flex items-center gap-1.5 min-w-0">
            <Icon size={14} className="text-emerald-700 flex-shrink-0" />
            <span className="text-slate-800 truncate">{value.label}</span>
            {value.sub && <span className="text-xs text-slate-500 truncate">· {value.sub}</span>}
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="text-slate-400 hover:text-slate-700 flex-shrink-0"
            aria-label="Remover vínculo"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={term}
            onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            autoFocus={autoFocus}
            placeholder={meta.placeholder}
            className="w-full pl-8 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {open && (loading || results.length > 0 || error || term.trim().length >= 2) && (
            <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {loading && <div className="px-3 py-2 text-xs text-slate-500">Buscando...</div>}
              {error && <div className="px-3 py-2 text-xs text-red-600">{error}</div>}
              {!loading && !error && results.length === 0 && term.trim().length >= 2 && (
                <div className="px-3 py-2 text-xs text-slate-500">Nenhum resultado.</div>
              )}
              {results.map((r) => (
                <button
                  type="button"
                  key={`${r.type}:${r.id}`}
                  onClick={() => handlePick(r)}
                  className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm border-b border-slate-100 last:border-b-0 flex items-center gap-2"
                >
                  <Icon size={12} className="text-slate-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-slate-800 truncate">{r.label}</div>
                    {r.sub && <div className="text-[11px] text-slate-500 truncate">{r.sub}</div>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
